import { format } from "date-fns";
import { saveAs } from "file-saver";
import * as Icons from "heroicons-react";
import { MaterialReactTable } from "material-react-table";
import { useEffect, useState } from "react";
import { CSVLink } from "react-csv";

import DatePicker from "react-datepicker";
import { Toaster } from "react-hot-toast";

import { useParams } from "react-router-dom";
import { useGetCustomerByIdQuery } from "../../services/customerApi";
import { useCustomerPurchaseHistoryQuery } from "../../services/saleApi";
import { useCustomerGroupDwQuery } from "../../services/customerGroupApi";
import { useUserDwQuery } from "../../services/userApi";
import Header from "../Common/Header/Header";
import LoadingModal from "../Common/Modal/LoadingModal";
import SideBar from "../Common/SideBar/SideBar";
import { signInUser } from "../Utility/Auth";
import "./Customer.css";

const CustomerLedger = () => {
  const auth = signInUser() || {};
  const { aamarId, storeSettings } = auth;
  const timeElapsed = Date.now();
  const today = new Date(timeElapsed);
  const { id } = useParams();
  // console.log("id",id)
  const [saleData, setSaleData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [startDate, setStartDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const [endDate, setEndDate] = useState(format(new Date(), "MM-dd-yyyy"));

  const { data, refetch } = useCustomerPurchaseHistoryQuery({
    start: startDate,
    end: endDate,
    id,
  });
  const { data: customerData } = useGetCustomerByIdQuery({
    id,
    aamarId,
  });
  const { data: customerGroups } = useCustomerGroupDwQuery();
  const { data: userList } = useUserDwQuery("");

  // Helper to reliably resolve biller display name
  const resolveBillerName = (item, currentAuth, users) => {
    if (!item) return "N/A";

    const explicitName =
      item?.billerName ||
      item?.biller?.name ||
      item?.billerInfo?.name ||
      item?.userName;
    if (
      explicitName &&
      explicitName !== "Unknown Biller" &&
      typeof explicitName === "string"
    ) {
      return explicitName;
    }

    const val = item?.billerId;
    if (
      val &&
      typeof val === "object" &&
      val?.name &&
      val.name !== "Unknown Biller"
    ) {
      return val.name;
    }

    if (
      typeof val === "string" &&
      val &&
      val !== "Unknown Biller" &&
      !/^[0-9a-fA-F]{24}$/.test(val)
    ) {
      return val;
    }

    const idToMatch =
      typeof val === "string"
        ? val
        : val?._id || val?.id || item?.userId || item?.biller;

    if (idToMatch) {
      if (
        idToMatch === currentAuth?.id ||
        idToMatch === currentAuth?._id ||
        idToMatch === currentAuth?.userId
      ) {
        return currentAuth?.name || currentAuth?.username || "N/A";
      }

      if (Array.isArray(users) && users.length > 0) {
        const found = users.find(
          (u) =>
            u?._id === idToMatch ||
            u?.id === idToMatch ||
            u?.username === idToMatch
        );
        if (found?.name) return found.name;
        if (found?.username) return found.username;
      }
    }

    return currentAuth?.name || currentAuth?.username || "N/A";
  };

  // Resolve customer group and type
  const matchedGroupById = customerGroups?.find(
    (g) =>
      g._id === customerData?.group ||
      g.id === customerData?.group ||
      g._id === customerData?.group?._id ||
      g.id === customerData?.group?._id
  );

  const matchedGroupByType = customerGroups?.find(
    (g) =>
      g.name?.trim().toLowerCase() === customerData?.type?.trim().toLowerCase() ||
      g._id === customerData?.type ||
      g.id === customerData?.type
  );

  const displayGroup =
    matchedGroupById?.name ||
    customerData?.group?.name ||
    (typeof customerData?.group === "string" &&
    !/^[0-9a-fA-F]{24}$/.test(customerData.group) &&
    customerData.group !== "no-Group"
      ? customerData.group
      : "") ||
    matchedGroupByType?.name ||
    (customerData?.type &&
    !["regular", "premium", "vip"].includes(
      customerData?.type?.trim().toLowerCase()
    )
      ? customerData.type
      : "") ||
    (customerData?.group ? "" : customerData?.type) ||
    "N/A";

  const isTypeHoldingGroup = Boolean(
    customerData?.type &&
      (customerData.type.trim().toLowerCase() ===
        displayGroup.trim().toLowerCase() ||
        matchedGroupByType)
  );

  const displayType = isTypeHoldingGroup
    ? "Regular"
    : customerData?.type || "Regular";

  const columns = [
    {
      accessorKey: "createdAt",
      header: "Date",
      size: 40,
    },
    { accessorKey: "invoiceNo", header: "Invoice" },
    { accessorKey: "totalItem", header: "Items", size: 30 },
    { accessorKey: "grossTotal", header: "Gross Total", size: 60 },
    { accessorKey: "vat", header: "VAT", size: 40 },
    { accessorKey: "total", header: "Total", size: 60 },
    { accessorKey: "oldPoint", header: "Old Point", size: 40 },
    { accessorKey: "todayPoint", header: "Today Point", size: 40 },
    { accessorKey: "newPoint", header: "New Point", size: 40 },
    { accessorKey: "promoDiscount", header: "Promo Discount", size: 40 },
    {
      accessorKey: "billerId",
      header: "Biller",
      Cell: ({ row }) => {
        return (
          row.original?.billerName ||
          resolveBillerName(row.original, auth, userList)
        );
      },
    },
    { accessorKey: "totalReceived", header: "Total Received", size: 60 },
    { accessorKey: "changeAmount", header: "Change Amount", size: 60 },
    { accessorKey: "cash", header: "Cash", size: 60 },
    { accessorKey: "cardType", header: "Card Type", size: 60 },
    { accessorKey: "cardAmount", header: "Card Amount", size: 60 },
    { accessorKey: "mfsType", header: "MFS Type", size: 60 },
    { accessorKey: "mfsAmount", header: "MFS Amount", size: 60 },
    { accessorKey: "pointUse", header: "Point Use", size: 60 },
    { accessorKey: "totalReturn", header: "Total Return", size: 60 },
    { accessorKey: "grosstotalReturn", header: "Gross Total Return", size: 60 },
    { accessorKey: "itemReturn", header: "Item Return", size: 60 },
    { accessorKey: "pointReturn", header: "Point Return", size: 60 },
  ];

  const exportHeader = columns.map((col) => ({
    label: col.header,
    key: col.accessorKey,
  }));

  // Sync sale data when API data changes
  useEffect(() => {
    if (data?.sale) {
      const formattedSales = data.sale.map((item) => {
        const resolvedName = resolveBillerName(item, auth, userList);
        return {
          ...item,
          billerId: resolvedName,
          billerName: resolvedName,
        };
      });
      setSaleData(formattedSales);
    }
    setLoader(false);
  }, [data, userList, auth?.id, auth?.name]);

  // Refetch data when date range or id changes
  useEffect(() => {
    if (startDate && endDate && id) {
      setLoader(false);
      refetch();
    }
  }, [startDate, endDate, id, refetch]);
  const preheader = [
    [`${storeSettings?.storeName || "No-Name"}`],
    [`${storeSettings?.address?.street || "No-Street"}`],
    [
      `${storeSettings?.address?.city || "No-City"}-${
        storeSettings?.address?.post || "No-PostalCode"
      }`,
    ],
    [
      `For the Period: ${format(new Date(startDate), "MM-dd-yyyy")} to ${format(
        new Date(endDate),
        "MM-dd-yyyy"
      )}`,
    ],
    ["Customer Ledger Details"],

    [], // Empty row for spacing
  ];

  const convertArrayOfObjectsToCSV = (array, preheader) => {
    if (!array || !array.length) return null;

    const headers = Object.keys(array[0]);
    const csvData = [
      ...preheader.map((row) => row.join(",")), // Add preheader rows
      headers.join(","), // Add header row
      ...array.map((row) =>
        headers
          .map((fieldName) => JSON.stringify(row[fieldName]) || "")
          .join(",")
      ),
    ];

    return csvData.join("\r\n");
  };

  const downloadCSV = () => {
    const csvString = convertArrayOfObjectsToCSV(saleData, preheader);
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `Customer details Export - [${today.toDateString()}].csv`);
  };
  return (
    <div className="container-fluid">
      <LoadingModal title="Please Wait" onShow={loader} />
      <div className="row">
        <div className="col-md-2">
          <SideBar />
        </div>
        <div className="col-md-10">
          <Header title="Customer Ledger" />
          {/* Date Picker Section */}
          <div className="row my-3">
            <div className="col-md-5">
              <form>
                <div className="date-picker d-flex align-items-center gap-2">
                  <DatePicker
                    selected={new Date(startDate)}
                    className="form-control"
                    onChange={(date) =>
                      setStartDate(format(new Date(date), "MM-dd-yyyy"))
                    }
                  />
                  <DatePicker
                    selected={new Date(endDate)}
                    className="form-control"
                    onChange={(date) =>
                      setEndDate(format(new Date(date), "MM-dd-yyyy"))
                    }
                  />
                </div>
              </form>
            </div>
          </div>
          {/* Customer Details Section */}
          <div className="row mt-md-4 mb-3">
            <div className="col-md-12">
              <div className="card p-3">
                <h4 className="mb-2">Customer Details</h4>
                <hr />
                <div className="row">
                  <div className="col-md-3">
                    <p>
                      <b>Name:</b> {customerData?.name}
                    </p>
                    <p>
                      <b>Username:</b> {customerData?.username || "N/A"}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <b>Phone:</b> {customerData?.phone}
                    </p>
                    <p>
                      <b>Email:</b> {customerData?.email}
                    </p>
                  </div>
                  <div className="col-md-2">
                    <p>
                      <b>Points:</b> {customerData?.point ?? 0}
                    </p>
                    <p>
                      <b>Type:</b> {displayType}
                    </p>
                  </div>
                  <div className="col-md-2">
                    <p>
                      <b>Membership:</b> {customerData?.membership || "N/A"}
                    </p>
                    <p>
                      <b>Group:</b> {displayGroup}
                    </p>
                  </div>
                  <div className="col-md-2">
                    <p>
                      <b>Batch:</b> {customerData?.batch || "N/A"}
                    </p>
                    <p>
                      <b>Status:</b> {customerData?.status}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Sales Data Table Section */}
          <div className="row mt-md-5">
            <div className="col-md-12">
              <MaterialReactTable
                columns={[
                  {
                    accessorKey: "serial", // Dummy key for serial numbers
                    header: "#",
                    size: 0,
                    Cell: ({ row }) => row.index + 1, // Adds serial number starting from 1
                  },
                  ...columns, // Include your existing columns here
                ]}
                data={saleData?.length > 0 ? saleData : []} // Pass empty array if no data
                initialState={{
                  density: "compact",
                  pagination: {
                    pageSize: 20, // Default page size to 20
                  },
                }}
                renderTopToolbarCustomActions={() => (
                  <div className="d-flex gap-3 flex-wrap align-items-center">
                    {saleData?.length > 0 ? (
                      <button className="btn btn-dark" onClick={downloadCSV}>
                        <Icons.DocumentTextOutline size={22} /> Export CSV
                      </button>
                    ) : (
                      <div className="btn btn-dark">
                        <Icons.DocumentTextOutline size={22} /> Loading Data...
                      </div>
                    )}
                  </div>
                )}
                enableRowSelection={false} // Disable row selection if needed
                renderEmptyRow={() => (
                  <div className="text-center text-muted">
                    <p>No data available</p> {/* Message shown when no data */}
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
};

export default CustomerLedger;
